import { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Box,
    Button,
    HStack,
    IconButton,
    Input,
    Spinner,
    Text,
    VStack,
} from "@chakra-ui/react";
import { HiArrowDown, HiArrowUp, HiPaperClip } from "react-icons/hi2";
import dayjs from "dayjs";

const buildResourceUrl = (candidateId, path) =>
    `${process.env.REACT_APP_BASE_URL || '/mlapi'}/Candidate/${candidateId}/attachments/file?name=${encodeURIComponent(path)}`;

const CandidateNotesTab = ({ notes, isLoading, onAddNote, candidateId, search, onSearchChange, sortDirection, onToggleSortDirection, highlightId, onHighlightClear }) => {
    const { t } = useTranslation("models");
    const itemRefs = useRef({});
    const [activeHighlight, setActiveHighlight] = useState(highlightId ?? null);

    useEffect(() => {
        if (!activeHighlight) return;
        const el = itemRefs.current[activeHighlight];
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [notes, activeHighlight]);

    useEffect(() => {
        if (!activeHighlight) return;
        const timer = setTimeout(() => {
            setActiveHighlight(null);
            onHighlightClear?.();
        }, 2500);
        return () => clearTimeout(timer);
    }, [activeHighlight]);

    return (
        <Box>
            <HStack justify="space-between" mb={4} gap={3} flexWrap="wrap">
                <Text color="text.grey" fontSize="sm" flexShrink={0}>
                    {!isLoading && t("candidateDetailPage.notesCount", { count: notes.length })}
                </Text>
                <HStack gap={2} flex={1} justify="flex-end">
                    <Input
                        placeholder={t("candidateDetailPage.notesSearchPlaceholder")}
                        value={search}
                        onChange={(e) => onSearchChange(e.target.value)}
                        size="sm"
                        maxW="240px"
                    />
                    <IconButton
                        variant="ghost"
                        size="sm"
                        aria-label={t("candidateDetailPage.notesSortToggle")}
                        onClick={onToggleSortDirection}
                    >
                        {sortDirection === 'desc' ? <HiArrowDown /> : <HiArrowUp />}
                    </IconButton>
                    <Button size="sm" variant="primary" onClick={onAddNote}>
                        {t("candidateDetailPage.addNote")}
                    </Button>
                </HStack>
            </HStack>
            {isLoading ? (
                <VStack py={8} align="center"><Spinner color="brand.400" size="sm" /></VStack>
            ) : notes.length === 0 ? (
                <VStack py={10} align="center">
                    <Text color="text.grey" fontSize="sm">
                        {search ? t("candidateDetailPage.notesNoResults") : t("candidateDetailPage.noNotes")}
                    </Text>
                </VStack>
            ) : (
                <VStack gap={3} align="stretch">
                    {notes.map((note) => (
                        <Box
                            key={note.id}
                            ref={el => { itemRefs.current[note.id] = el; }}
                            bg="bg.card"
                            border="1px solid"
                            borderColor={activeHighlight === note.id ? "brand.400" : "border.primary"}
                            borderRadius={12}
                            p={5}
                            transition="border-color 0.5s"
                            boxShadow={activeHighlight === note.id ? "0 0 0 3px color-mix(in srgb, var(--chakra-colors-brand-400) 30%, transparent)" : undefined}
                        >
                            <HStack gap={3} mb={3}>
                                <Box
                                    w="36px" h="36px"
                                    borderRadius="full"
                                    bg="brand.400"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    flexShrink={0}
                                >
                                    <Text color="white" fontWeight={700} fontSize="sm">
                                        {(note.authorFullName || "?")[0].toUpperCase()}
                                    </Text>
                                </Box>
                                <VStack align="flex-start" gap={0}>
                                    <Text color="white" fontWeight={600} fontSize="sm">{note.authorFullName}</Text>
                                    <Text color="text.grey" fontSize="xs">{dayjs(note.createdAt).format("DD.MM.YYYY HH:mm")}</Text>
                                </VStack>
                            </HStack>
                            <Text color="white" fontSize="sm" lineHeight={1.6}>{note.content}</Text>
                            {note.resources?.length > 0 && (
                                <HStack flexWrap="wrap" gap={2} mt={2}>
                                    {note.resources.map((r, idx) => (
                                        <HStack
                                            key={idx}
                                            as="a"
                                            href={buildResourceUrl(candidateId, r.path)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            gap={2} px={3} py={2}
                                            bg="bg.secondary" borderRadius={8}
                                            cursor="pointer" _hover={{ bg: "bg.hover" }}
                                        >
                                            <HiPaperClip style={{ flexShrink: 0 }} />
                                            <Text fontSize="xs" color="text.grey" whiteSpace="nowrap">{r.name}</Text>
                                        </HStack>
                                    ))}
                                </HStack>
                            )}
                        </Box>
                    ))}
                </VStack>
            )}
        </Box>
    );
};

export default CandidateNotesTab;
